/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { TableDisplayColumn } from './TableDisplayBase';
import { TableDisplayFooterCell } from './TableDisplayFooterCell';
import { backgroundStyles, tableFooterRowStyles } from '../PRIVATE_Table/themes/Table.css';

/**
 * Props for the TableDisplayFooter Component
 */
export type TableDisplayFooterProps<K, D, C> = {
  tableId: string;
  isRendered: boolean;
  columnsArray: TableDisplayColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  stickyBottomOffset?: number;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
  dataRowCount: number;
};

/**
 * The internal component used to render a footer region in Table.
 */
export function TableDisplayFooter<K, D, C>({
  tableId,
  isRendered,
  columnsArray,
  hasVerticalGridlines,
  stickyBottomOffset,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  dataRowCount,
  isRtl,
  isHighContrast
}: TableDisplayFooterProps<K, D, C>) {
  const rowClasses = classNames([tableFooterRowStyles.base]);

  return isRendered ? (
    <tfoot class={backgroundStyles.inherit} role={'presentation'}>
      <tr
        aria-rowindex={Math.max(dataRowCount, 1) + 2}
        class={rowClasses}
        role={'row'}
        data-oj-table-footer-row={tableId}>
        {columnsArray.map((column: TableDisplayColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          const isSticky = column.value.sticky === 'enabled';
          let isFirstLogicalColumnIndex = false;
          let isFinalLogicalColumnIndex = false;
          if (isSticky) {
            if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
              isFirstLogicalColumnIndex = true;
            }
            if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
              isFinalLogicalColumnIndex = true;
            }
          }
          const footerCellProps = {
            tableId: tableId,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
            isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
            hasVerticalGridlines: hasVerticalGridlines,
            stickyBottomOffset: stickyBottomOffset,
            isSticky: isSticky,
            hasStartEdge: columnBorderEdges[columnIndex].hasStart,
            hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
            footerText: column.value.footerText,
            renderer: column.value.footerRenderer,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isRtl: isRtl,
            isHighContrast: isHighContrast,
            padding: column.value.footerPadding,
            horizontalAlignment:
              column.value.footerAlignment?.horizontal != null
                ? column.value.footerAlignment.horizontal
                : column.value.horizontalAlignment,
            verticalAlignment: column.value.footerAlignment?.vertical
          };
          return <TableDisplayFooterCell {...footerCellProps} />;
        })}
      </tr>
    </tfoot>
  ) : null;
}
